import {
  BimBcfCamera,
  BimBcfClippingPlane,
  BimClashResult,
  BimViewerAdapter,
  BimViewerCallbacks,
  BimViewerHandle,
  BimLoadPhase,
  BimModelSource,
  BimTreeNode,
  BimEntityProperties,
  BimMeasureMode,
  BimProjectionMode,
  BimSectionPlane
} from 'Data/interfaces/BimViewer/IBimViewerPort';

const VIEWER_VERSION = 'v1.0.0';
const SCRIPT_SRC = `/coordly3DViewer/${VIEWER_VERSION}/initCoordly3DViewer.js`;

// O front não fala com o server de parse: ele é exclusivo do backend, que
// entrega a geometria já tesselada como artefatos no Blob. Por isso a ponte
// recebe só artefatos — nem `fileUrl` nem `serverUrl`.
interface IfcLiteBridgeConfig {
  fileName?: string;
  artifacts?: BimModelSource['artifacts'];
  container?: HTMLElement;
  federated?: boolean;
}
interface IfcLiteInstance { dispose(): void; }
type IfcLiteInit = (config: IfcLiteBridgeConfig) => IfcLiteInstance;

// Carrega o bundle uma vez (imutável) via <script src> — não textContent — pros
// imports dinâmicos do bundle resolverem contra a URL do script, não do documento.
const loadEngineScript = (() => {
  let promise: Promise<void> | null = null;
  return () => {
    if ((window as any).initCoordly3DViewer) { return Promise.resolve(); }
    if (promise) { return promise; }
    promise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = SCRIPT_SRC;
      script.onload = () => resolve();
      script.onerror = () => { promise = null; reject(new Error('falha ao carregar o motor 3D')); };
      document.head.appendChild(script);
    });
    return promise;
  };
})();

// Adaptador ifc-lite: ÚNICO ponto do web/ que conhece a ponte (window.bim* /
// initCoordly3DViewer / eventos bim-*). Traduz tudo pra porta BimViewerAdapter.
export class IfcLiteViewerAdapter implements BimViewerAdapter {
  async mount(
    container: HTMLElement,
    source: BimModelSource,
    callbacks: BimViewerCallbacks
  ): Promise<BimViewerHandle> {
    const onProgress = (e: Event) =>
      callbacks.onLoadProgress?.((e as CustomEvent).detail?.phase as BimLoadPhase);
    const onLoaded = (e: Event) => callbacks.onLoaded?.((e as CustomEvent).detail);
    const onError = (e: Event) => callbacks.onError?.((e as CustomEvent).detail);
    const onSelection = (e: Event) => callbacks.onSelectionChanged?.((e as CustomEvent).detail);
    const onDataModel = (e: Event) => callbacks.onDataModelReady?.((e as CustomEvent).detail);
    const onDataModelProgress = (e: Event) => callbacks.onDataModelProgress?.((e as CustomEvent).detail);
    const onMeasure = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      callbacks.onMeasureChanged?.({
        mode: detail?.mode ?? 'none',
        measurements: detail?.measurements ?? [],
      });
    };

    const onViewReset = () => callbacks.onViewReset?.();
    const onProjection = (e: Event) => {
      const mode = (e as CustomEvent).detail?.mode;
      if (mode) { callbacks.onProjectionChanged?.(mode); }
    };

    window.addEventListener('bim-measure-changed', onMeasure);
    window.addEventListener('bim-view-reset', onViewReset);
    const onSection = (e: Event) =>
      callbacks.onSectionChanged?.((e as CustomEvent).detail?.section ?? null);

    window.addEventListener('bim-projection-changed', onProjection);
    window.addEventListener('bim-section-changed', onSection);
    window.addEventListener('bim-load-progress', onProgress);
    window.addEventListener('bim-file-loaded', onLoaded);
    window.addEventListener('bim-load-error', onError);
    window.addEventListener('bim-selection-changed', onSelection);
    window.addEventListener('bim-datamodel-ready', onDataModel);
    window.addEventListener('bim-datamodel-progress', onDataModelProgress);

    const detach = () => {
      window.removeEventListener('bim-load-progress', onProgress);
      window.removeEventListener('bim-file-loaded', onLoaded);
      window.removeEventListener('bim-load-error', onError);
      window.removeEventListener('bim-selection-changed', onSelection);
      window.removeEventListener('bim-datamodel-ready', onDataModel);
      window.removeEventListener('bim-datamodel-progress', onDataModelProgress);
      window.removeEventListener('bim-measure-changed', onMeasure);
      window.removeEventListener('bim-view-reset', onViewReset);
      window.removeEventListener('bim-projection-changed', onProjection);
      window.removeEventListener('bim-section-changed', onSection);
    };

    try {
      await loadEngineScript();
    } catch (err) {
      detach();
      throw err;
    }

    const init = (window as any).initCoordly3DViewer as IfcLiteInit;
    const instance = init({
      fileName: source.fileName,
      artifacts: source.artifacts,
      container,
      // Sem artefatos = cena vazia p/ federação (o app chama addModel).
      federated: !source.artifacts
    });

    // `bimHelpers` é global e trocado a cada mount; ler no momento da chamada
    // (não capturar) mantém o handle apontando pro motor vivo.
    const helpers = () => (window as any).bimHelpers;

    return {
      fitToView: () => helpers()?.fitToView?.(),
      addModel: (s, modelId) =>
        s.artifacts
          ? helpers()?.addModel?.(s.artifacts, modelId) ?? Promise.resolve()
          : Promise.resolve(),
      removeModel: (modelId) => helpers()?.removeModel?.(modelId),
      // `true` no fallback: um bundle antigo (sem hasModel) não sabe responder, e
      // assumir "não entrou" faria o app re-adicionar em loop o que já está lá.
      hasModel: (modelId) => helpers()?.hasModel?.(modelId) ?? true,
      clearModels: () => helpers()?.clearModels?.(),
      getSpatialTree: (modelIndex): BimTreeNode[] =>
        helpers()?.getSpatialTree?.(modelIndex) ?? [],
      // `Promise.resolve` aceita as duas formas: o bundle antigo responde na hora,
      // o novo (data model num worker) devolve Promise.
      getEntityProperties: async (expressId, modelIndex): Promise<BimEntityProperties | null> =>
        (await helpers()?.getEntityProperties?.(expressId, modelIndex)) ?? null,
      getEntitiesProperties: async (expressIds, modelIndex): Promise<BimEntityProperties[]> => {
        const h = helpers();
        if (h?.getEntitiesProperties) { return (await h.getEntitiesProperties(expressIds, modelIndex)) ?? []; }
        // Bundle antigo, sem o lote: um elemento por vez, como era.
        const entities = await Promise.all(
          expressIds.map((expressId) => Promise.resolve(h?.getEntityProperties?.(expressId, modelIndex))),
        );
        return entities.filter((entity): entity is BimEntityProperties => !!entity);
      },
      getEntityLabels: async (expressIds, modelIndex) =>
        (await helpers()?.getEntityLabels?.(expressIds, modelIndex)) ?? [],
      select: (expressId, opts) => helpers()?.select?.(expressId, opts),
      selectEntities: (expressIds, opts) => helpers()?.selectEntities?.(expressIds, opts),
      clearSelection: () => helpers()?.clearSelection?.(),
      setMultiSelect: (enabled) => helpers()?.setMultiSelect?.(enabled),
      setGhostMode: (enabled) => helpers()?.setGhostMode?.(enabled),
      focusSelection: () => helpers()?.focusSelection?.(),
      frameEntities: (expressIds, modelIndex) =>
        helpers()?.frameEntities?.(expressIds, modelIndex),
      isolate: (expressIds) => helpers()?.isolate?.(expressIds),
      hide: (expressIds) => helpers()?.hide?.(expressIds),
      show: (expressIds) => helpers()?.show?.(expressIds),
      // Bundle antigo não tem `ghost`: o botão não faz nada, sem quebrar a tela.
      ghost: (expressIds) => helpers()?.ghost?.(expressIds),
      clearGhost: () => helpers()?.clearGhost?.(),
      showAll: () => helpers()?.showAll?.(),
      // Bundle antigo não tem `resetView`: o Esc dele ainda limpa a seleção.
      resetView: () => helpers()?.resetView?.(),
      setSectionPlane: (section: BimSectionPlane | null) => helpers()?.setSectionPlane?.(section),
      // Bundle antigo não tem estes membros: devolver vazio degrada em "sem
      // viewpoint", que é o que a UI sabe tratar — melhor que quebrar a tela.
      getCamera: (): BimBcfCamera | null => helpers()?.getCamera?.() ?? null,
      setCamera: (camera, targetDistance) => helpers()?.setCamera?.(camera, targetDistance),
      getProjection: (): BimProjectionMode | null => helpers()?.getProjection?.() ?? null,
      setProjection: (mode) => helpers()?.setProjection?.(mode),
      getClippingPlanes: (): BimBcfClippingPlane[] | null => helpers()?.getClippingPlanes?.() ?? null,
      setClippingPlanes: (planes) => helpers()?.setClippingPlanes?.(planes),
      captureSnapshot: (): Promise<string | null> =>
        Promise.resolve(helpers()?.captureSnapshot?.() ?? null),
      getGlobalIds: async (expressIds, modelIndex) =>
        (await helpers()?.getGlobalIds?.(expressIds, modelIndex)) ?? [],
      getExpressIds: async (globalIds, modelIndex) =>
        (await helpers()?.getExpressIds?.(globalIds, modelIndex)) ?? [],
      setMeasureMode: (mode: BimMeasureMode) => helpers()?.setMeasureMode?.(mode),
      deleteMeasurement: (id: string) => helpers()?.deleteMeasurement?.(id),
      clearMeasurements: () => helpers()?.clearMeasurements?.(),
      runClash: async (rules, options, onProgress): Promise<BimClashResult | null> =>
        (await helpers()?.runClash?.(rules, options, onProgress)) ?? null,
      cancelClash: () => helpers()?.cancelClash?.(),
      focusClash: (a, b, mode) => helpers()?.focusClash?.(a, b, mode),
      setClashHighlight: (enabled) => helpers()?.setClashHighlight?.(enabled),
      clearClash: () => helpers()?.clearClash?.(),
      getClashTypes: (modelIds) => helpers()?.getClashTypes?.(modelIds) ?? [],
      dispose: () => { detach(); instance.dispose(); }
    };
  }
}
